import { inject, Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { CorrelationsMockService } from '@mock/data-services/correlations-mock.service';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import {
  ICorrelationSpectrumChartData,
  ICorrelationSpectrumChartDataPoint,
} from '@app/api/interfaces/chart-data/correlation-spectrum';

@Injectable({ providedIn: 'root' })
export class CorrelationSpectrumMockService extends MockService {
  private correlationsMockService = inject(CorrelationsMockService);

  public get(
    projectId: string,
    correlationId: string
  ): Observable<IApiResponse<ICorrelationSpectrumChartData, object>> {
    const correlation = this.correlationsMockService.correlations.find(el => el.id === correlationId);

    const values: ICorrelationSpectrumChartDataPoint[] = correlation
      ? this.generateAccordingToCorrelation(correlation)
      : [];
    const data: ICorrelationSpectrumChartData = { values };

    return this.mockResponse(
      'CORRELATION_SPECTRUM:get',
      {
        data,
        meta: {},
      },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generateAccordingToCorrelation(corelation: ICorrelation): ICorrelationSpectrumChartDataPoint[] {
    const frequencyStep = 4;
    const frequencyMin = 0;
    const frequencyMax = 508;

    const charSum = convertStringInToSumOfAllCharacters(corelation.id);

    const spectrumMedian = charSum % 15 || 15;
    const spectrumMax = spectrumMedian * 3;

    const pickValue1 = Math.min(spectrumMedian * ((charSum % 3) + 1.5), spectrumMax);
    const pickValue2 = Math.min(spectrumMedian * ((charSum % 2) + 2), spectrumMax);
    const pickValue3 = Math.min(spectrumMedian * ((charSum % 4) + 1.2), spectrumMax);

    const segment = frequencyMax / 4;
    const pick1Freq = segment + (charSum % 50);
    const pick2Freq = 2 * segment + (charSum % 50);
    const pick3Freq = 3 * segment + (charSum % 50);

    const jaggedAmplitude = spectrumMedian * 0.3;
    const jaggedPeriod = 3;

    const spectrum: ICorrelationSpectrumChartDataPoint[] = [];
    const pointsCount = Math.floor((frequencyMax - frequencyMin) / frequencyStep);

    for (let i = 0; i <= pointsCount; i++) {
      const currentFreq = frequencyMin + i * frequencyStep;
      let baseValue = 0;

      if (currentFreq < 30) {
        const dropRatio = currentFreq / 30;
        baseValue = spectrumMax - dropRatio * (spectrumMax - spectrumMedian);
      } else {
        const jaggedOffset = ((i % jaggedPeriod) / jaggedPeriod) * jaggedAmplitude;
        baseValue = spectrumMedian + jaggedOffset;
      }

      const peakWidth = 12;
      const p1 = pickValue1 * Math.exp(-Math.pow(currentFreq - pick1Freq, 2) / (2 * Math.pow(peakWidth, 2)));
      const p2 = pickValue2 * Math.exp(-Math.pow(currentFreq - pick2Freq, 2) / (2 * Math.pow(peakWidth, 2)));
      const p3 = pickValue3 * Math.exp(-Math.pow(currentFreq - pick3Freq, 2) / (2 * Math.pow(peakWidth, 2)));

      let finalValue = Math.max(baseValue, p1, p2, p3);
      finalValue = Math.min(finalValue, spectrumMax);

      spectrum.push({
        frequency: currentFreq,
        spectrumDbValue: Number(finalValue.toFixed(6)),
      });
    }

    return spectrum;
  }
}
